import {
  formatOdds,
  resolveApiBase,
  SportsApiClient,
  text,
  toArray,
  type OddsFormat,
  type WidgetProps,
} from '@sports/core'
import { useState } from 'preact/hooks'
import { useFeed } from '../../use-feed'

export const RACECARDS_ATTRIBUTES = [
  'lang',
  'region',
  'limit',
  'heading',
  'refresh',
  'api-base',
  'theme',
] as const

interface RacecardsProps extends WidgetProps {
  lang?: string
  region?: string
  limit?: string
  heading?: string
  refresh?: string
  apiBase?: string
}

interface BookmakerResponse {
  oddsFormat: OddsFormat
  brand: { link?: string | null } | null
}

/**
 * Region → the display country, its flag, and the feed path. The passthrough
 * route rejects `?`, so only today's per-region cards are offered here.
 */
const REGIONS: Record<string, { country: string; flag: string }> = {
  uk: { country: 'United Kingdom', flag: '🇬🇧' },
  usa: { country: 'USA', flag: '🇺🇸' },
  australia: { country: 'Australia', flag: '🇦🇺' },
  hk: { country: 'Hong Kong', flag: '🇭🇰' },
  singapore: { country: 'Singapore', flag: '🇸🇬' },
  dubai: { country: 'UAE', flag: '🇦🇪' },
  sa: { country: 'South Africa', flag: '🇿🇦' },
  france: { country: 'France', flag: '🇫🇷' },
  sweden: { country: 'Sweden', flag: '🇸🇪' },
}

export const racecardsStyles = `
  .rc-venues {
    display: flex;
    gap: 0.4rem;
    overflow-x: auto;
    padding: 0.5rem 0.75rem;
    background: var(--sports-header);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    scrollbar-width: none;
  }
  .rc-venues::-webkit-scrollbar { display: none; }
  .rc-venue {
    flex: 0 0 auto;
    padding: 0.25rem 0.6rem;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: transparent;
    color: var(--sports-header-fg);
    opacity: 0.7;
    font: inherit;
    font-size: 0.72rem;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
  }
  .rc-venue.active { background: var(--sports-header-fg); color: var(--sports-header); opacity: 1; }

  .rc-times {
    display: flex;
    gap: 0.35rem;
    overflow-x: auto;
    padding: 0.5rem 0.75rem;
    background: var(--sports-muted-bg);
    border-bottom: 1px solid var(--sports-border);
    scrollbar-width: none;
  }
  .rc-times::-webkit-scrollbar { display: none; }
  .rc-time {
    flex: 0 0 auto;
    min-width: 3.2rem;
    padding: 0.3rem 0.55rem;
    border-radius: 6px;
    border: 1px solid transparent;
    background: var(--sports-card);
    color: var(--sports-fg);
    font: inherit;
    font-size: 0.8rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    text-align: center;
    white-space: nowrap;
    cursor: pointer;
  }
  .rc-time:hover { border-color: var(--sports-border); }
  .rc-time.active {
    background: var(--sports-accent);
    border-color: var(--sports-accent);
    color: #fff;
  }

  .rc-subhead {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.6rem 1rem 0.2rem;
    font-size: 0.85rem;
    font-weight: 600;
  }
  .rc-subhead .title { display: flex; align-items: center; gap: 0.4rem; overflow: hidden; }
  .rc-subhead .title span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .rc-subhead .start {
    color: var(--sports-muted);
    font-weight: 400;
    font-size: 0.75rem;
    white-space: nowrap;
    flex: 0 0 auto;
  }
  .rc-meta {
    padding: 0 1rem 0.5rem;
    font-size: 0.75rem;
    color: var(--sports-muted);
  }

  table.rc-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
  table.rc-table th, table.rc-table td {
    padding: 0.5rem 0.5rem;
    border-bottom: 1px solid var(--sports-border);
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  table.rc-table th {
    color: var(--sports-muted);
    font-weight: 600;
    font-size: 0.66rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  table.rc-table th.rc-l, table.rc-table td.rc-l { text-align: left; font-variant-numeric: normal; }
  table.rc-table td.rc-num { color: var(--sports-muted); width: 1.5rem; }
  table.rc-table td.rc-horse { white-space: normal; }
  table.rc-table .rc-horse .name { font-weight: 600; color: var(--sports-fg); }
  table.rc-table .rc-people { color: var(--sports-muted); font-size: 0.78rem; white-space: normal; }
  table.rc-table tbody tr:last-child td { border-bottom: 0; }

  .rc-sp {
    display: inline-block;
    min-width: 2.8rem;
    padding: 0.15rem 0.4rem;
    border: 1px solid var(--sports-border);
    border-radius: 6px;
    text-align: center;
    text-decoration: none;
    color: var(--sports-fg);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  a.rc-sp:hover { border-color: var(--sports-accent); color: var(--sports-accent); }

  @container (max-width: 440px) {
    table.rc-table th.rc-hide, table.rc-table td.rc-hide { display: none; }
    table.rc-table th, table.rc-table td { padding: 0.45rem 0.35rem; }
  }
`

interface Runner {
  number: string
  name: string
  jockey: string
  trainer: string
  age: string
  weight: string
  distance: string
  sp: string
}

interface RaceCard {
  id: string
  name: string
  time: string
  startDate: string
  distance: string
  going: string
  raceClass: string
  runners: Runner[]
}

interface Meeting {
  id: string
  venue: string
  going: string
  date: string
  races: RaceCard[]
}

/** First non-blank decimal price across a horse's bookmakers. */
function firstOdd(oddsHorse: Record<string, unknown>): string {
  const bms = toArray<Record<string, unknown>>(
    (oddsHorse.bookmakers as Record<string, unknown> | undefined)?.bookmaker as
      | Record<string, unknown>[]
      | undefined,
  )
  for (const bm of bms) {
    const odd = text(bm, 'odd')
    if (odd && Number(odd) > 1) return odd
  }
  return ''
}

/**
 * `horse-racing/{region}`: `scores.tournament[]` is one meeting (venue, going),
 * holding `race[]`. Each race carries the field in `runners.horse[]` (name,
 * jockey, trainer, age, weight) and prices in a parallel `odds.horse[]`, joined
 * by the runner `number`.
 */
function parseMeetings(payload: unknown): Meeting[] {
  const scores = (payload as { scores?: { tournament?: unknown } })?.scores
  const out: Meeting[] = []
  for (const meeting of toArray<Record<string, unknown>>(
    scores?.tournament as Record<string, unknown> | Record<string, unknown>[] | undefined,
  )) {
    const meetingGoing = text(meeting, 'going')
    const date = text(meeting, 'date')
    const races: RaceCard[] = toArray<Record<string, unknown>>(
      meeting.race as Record<string, unknown>[] | undefined,
    )
      .map((race, index) => {
        const priceByNumber = new Map<string, string>()
        for (const oddsHorse of toArray<Record<string, unknown>>(
          (race.odds as Record<string, unknown> | undefined)?.horse as
            | Record<string, unknown>[]
            | undefined,
        )) {
          const key = text(oddsHorse, 'number') || text(oddsHorse, 'id')
          const odd = firstOdd(oddsHorse)
          if (key && odd) priceByNumber.set(key, odd)
        }
        const runners = toArray<Record<string, unknown>>(
          (race.runners as Record<string, unknown> | undefined)?.horse as
            | Record<string, unknown>[]
            | undefined,
        ).map((horse) => {
          const num = text(horse, 'number')
          return {
            number: num,
            name: text(horse, 'name'),
            jockey: text(horse, 'jockey'),
            trainer: text(horse, 'trainer'),
            age: text(horse, 'age'),
            weight: text(horse, 'wgt', 'wgt_lbs'),
            distance: text(horse, 'distance'),
            sp: priceByNumber.get(num) ?? priceByNumber.get(text(horse, 'id')) ?? '',
          }
        })
        return {
          id: text(race, 'id') || String(index),
          name: text(race, 'name'),
          time: text(race, 'time', 'offAt'),
          startDate: date,
          distance: text(race, 'distance'),
          going: meetingGoing,
          raceClass: text(race, 'class'),
          runners,
        }
      })
      .filter((race) => race.runners.length > 0)
    if (races.length === 0) continue
    out.push({
      id: text(meeting, 'id'),
      venue: text(meeting, 'name'),
      going: meetingGoing,
      date,
      races,
    })
  }
  return out
}

function number(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

/** "07.09.2026" → "07.09." for the compact start-time label. */
function shortDate(date: string): string {
  const parts = date.split('.')
  return parts.length >= 2 ? `${parts[0]}.${parts[1]}.` : date
}

export function Racecards({
  lang = 'es',
  region = 'uk',
  limit,
  heading,
  refresh,
  apiBase,
}: RacecardsProps) {
  const feedPath = REGIONS[region] ? region : 'uk'
  const info = REGIONS[feedPath]
  const maxMeetings = number(limit, 12)
  const refreshSeconds = number(refresh, 120)
  const base = resolveApiBase(apiBase)

  const { data, error, loading } = useFeed(
    (signal) => new SportsApiClient(base).feed('horse-racing', feedPath, signal),
    [base, feedPath],
    refreshSeconds,
  )
  // SP prices become the affiliate-linked odds cell; brand + format per locale.
  const { data: display } = useFeed(
    (signal) => new SportsApiClient(base).bookmaker<BookmakerResponse>(lang, 'match-center', signal),
    [base, lang],
    0,
  )
  const format: OddsFormat = display?.oddsFormat ?? 'fractional'
  const link = display?.brand?.link ?? undefined

  const [selVenue, setSelVenue] = useState<string | null>(null)
  const [selRace, setSelRace] = useState<string | null>(null)

  const meetings = (data ? parseMeetings(data) : []).slice(0, maxMeetings)
  const meeting = meetings.find((m) => m.id === selVenue) ?? meetings[0]
  const race = meeting?.races.find((r) => r.id === selRace) ?? meeting?.races[0]

  return (
    <div class="sports-card">
      <div class="sports-head">
        <span>
          {info.flag}{' '}
          {heading ?? `${info.country.toUpperCase()}${meeting ? `: ${meeting.venue.toUpperCase()}` : ''}`}
        </span>
      </div>

      {loading && <div class="sports-note">Loading…</div>}
      {!loading && error && (
        <div class="sports-note error">Racecards are unavailable right now.</div>
      )}
      {!loading && !error && meetings.length === 0 && (
        <div class="sports-note">No meetings today.</div>
      )}

      {meetings.length > 1 && (
        <div class="rc-venues">
          {meetings.map((m) => (
            <button
              key={m.id}
              type="button"
              class={m.id === meeting?.id ? 'rc-venue active' : 'rc-venue'}
              onClick={() => {
                setSelVenue(m.id)
                setSelRace(null)
              }}
            >
              {m.venue}
            </button>
          ))}
        </div>
      )}

      {meeting && meeting.races.length > 0 && (
        <div class="rc-times">
          {meeting.races.map((r) => (
            <button
              key={r.id}
              type="button"
              class={r.id === race?.id ? 'rc-time active' : 'rc-time'}
              onClick={() => setSelRace(r.id)}
            >
              {r.time || '—'}
            </button>
          ))}
        </div>
      )}

      {meeting && race && (
        <>
          <div class="rc-subhead">
            <span class="title">
              {info.flag}
              <span>
                {meeting.venue}: {race.name}
              </span>
            </span>
            {race.time && (
              <span class="start">
                Start time: {shortDate(race.startDate)} {race.time}
              </span>
            )}
          </div>
          <div class="rc-meta">
            {[race.distance, race.raceClass, race.going].filter(Boolean).join(' · ')}
          </div>

          <table class="rc-table">
            <thead>
              <tr>
                <th class="rc-l">#</th>
                <th class="rc-l">Horse</th>
                <th class="rc-l rc-hide">Jockey / Trainer</th>
                <th class="rc-hide">Age</th>
                <th class="rc-hide">Wgt</th>
                <th class="rc-hide">Dist.</th>
                <th>SP</th>
              </tr>
            </thead>
            <tbody>
              {race.runners.map((runner) => {
                const sp = formatOdds(runner.sp, format) || runner.sp
                return (
                  <tr key={runner.number || runner.name}>
                    <td class="rc-l rc-num">{runner.number}</td>
                    <td class="rc-l rc-horse">
                      <span class="name">{runner.name}</span>
                    </td>
                    <td class="rc-l rc-people rc-hide">
                      {[runner.jockey, runner.trainer].filter(Boolean).join(' / ')}
                    </td>
                    <td class="rc-hide">{runner.age || '—'}</td>
                    <td class="rc-hide">{runner.weight || '—'}</td>
                    <td class="rc-hide">{runner.distance || '—'}</td>
                    <td>
                      {sp ? (
                        link ? (
                          <a class="rc-sp" href={link} target="_blank" rel="sponsored noopener">
                            {sp}
                          </a>
                        ) : (
                          <span class="rc-sp">{sp}</span>
                        )
                      ) : (
                        <span class="rc-sp">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}
